import { t, type I18nLocale } from '@ardoq/i18n';
import { minimumDisplayedGridUnitsForGridPrecision } from './consts';
import type { TimelineSettingsLabels } from './types';
import type { TimelinesGridPrecisionUnit } from '@ardoq/api-types';

export const buildTimelineSettingsLabels = (
  i18nLocale: I18nLocale
): TimelineSettingsLabels => ({
  zoomIn: t(i18nLocale, 'timeline2024:settings.zoomIn', 'Zoom in'),
  zoomOut: t(i18nLocale, 'timeline2024:settings.zoomOut', 'Zoom out'),
  resetZoom: t(i18nLocale, 'timeline2024:settings.resetZoom', 'Reset zoom'),
  today: t(i18nLocale, 'timeline2024:settings.today', 'Today'),
  todayNotInRangePopover: t(
    i18nLocale,
    'timeline2024:settings.todayNotInRangePopover',
    'Today is not within the chart range'
  ),
  noScrollPossiblePopover: t(
    i18nLocale,
    'timeline2024:settings.noScrollPossiblePopover',
    'Not available at this zoom level'
  ),
  selectTimelineLayout: t(
    i18nLocale,
    'timeline2024:settings.selectTimelineLayout',
    'Select Timeline layout'
  ),
  useNewGrouping: t(
    i18nLocale,
    'timeline2024:settings.useNewGrouping',
    'Use new grouping'
  ),
  selectPhases: t(
    i18nLocale,
    'timeline2024:settings.selectPhases',
    'Select phases'
  ),
  showMilestones: t(
    i18nLocale,
    'timeline2024:settings.showMilestones',
    'Show milestones'
  ),
  collapseAll: t(
    i18nLocale,
    'timeline2024:settings.collapseAll',
    'Collapse all'
  ),
  expandAll: t(i18nLocale, 'timeline2024:settings.expandAll', 'Expand all'),
  hideAll: t(i18nLocale, 'timeline2024:settings.hideAll', 'Hide all'),
  timelineLayoutSelectLabel: t(
    i18nLocale,
    'timeline2024:settings.timelineLayoutSelectLabel',
    'Timeline layout'
  ),
  dateRangeLabel: t(
    i18nLocale,
    'timeline2024:settings.dateRangeLabel',
    'Date range'
  ),
  startDatePlaceholder: t(
    i18nLocale,
    'timeline2024:settings.startDatePlaceholder',
    'Start date...'
  ),
  endDatePlaceholder: t(
    i18nLocale,
    'timeline2024:settings.endDatePlaceholder',
    'End date...'
  ),
  autoLayoutInfoPopover: t(
    i18nLocale,
    'timeline2024:settings.autoLayoutInfoPopover',
    'This automatically sets the optimal Timeline layout based on the available date range.'
  ),
  gridPrecisionAutoLayout: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.autoLayout',
    'Auto-layout'
  ),
  gridPrecisionDaily: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.daily',
    'Daily'
  ),
  gridPrecisionWeekly: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.weekly',
    'Weekly'
  ),
  gridPrecisionMonthly: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.monthly',
    'Monthly'
  ),
  gridPrecisionQuarterly: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.quarterly',
    'Quarterly'
  ),
  gridPrecisionYearly: t(
    i18nLocale,
    'timeline2024:settings.gridPrecision.yearly',
    'Yearly'
  ),
  getDisabledOptionsPopoverContent: (
    value: TimelinesGridPrecisionUnit,
    label: string
  ) =>
    t(
      i18nLocale,
      'timeline2024:settings.disabledGridOptionPopover',
      {
        count: minimumDisplayedGridUnitsForGridPrecision[value],
        unit: value,
        label: label.toLowerCase(),
      },
      'Select a date range of at least {{count}} {{unit}} for the {{label}} timeline layout'
    ),
});

export const getLegendTitles = (
  i18nLocale: I18nLocale
): { phasesTitle: string; milestonesTitle: string } => ({
  phasesTitle: t(i18nLocale, 'timeline2024:legend.phases', 'Phases'),
  milestonesTitle: t(
    i18nLocale,
    'timeline2024:legend.milestones',
    'Milestones'
  ),
});
